import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ChevronDown, Loader2, X, Check, PlusCircle, MapPin } from 'lucide-react';
import axiosInstance from '../../../lib/axios';

const COUNTRY_CODES = [
  { flag: '🇮🇳', name: 'India', code: '+91' },
  { flag: '🇺🇸', name: 'United States', code: '+1' },
];

const JOB_ROLES = [
  'Practice Owner',
  'Senior Veterinarian',
  'Head Veterinarian',
  'Junior Veterinarian',
  'Other',
];

const PROFESSIONAL_CATEGORIES = [
  'General Veterinarian',
  'Specialist',
  'Intern/Resident',
  'Other',
];

const PRIMARY_PRACTICES = [
  'Small Animal',
  'Large Animal',
  'Mixed Practice',
  'Exotic Animal',
  'Other',
];

const CustomSelect = ({
  options,
  value,
  onChange,
  placeholder,
  error,
  dropUp = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3.5 py-2.5 bg-[#eef0f4] rounded-xl text-sm transition-all text-left border ${error
          ? 'border-red-500 ring-1 ring-red-500'
          : 'border-gray-300/80 hover:border-gray-400 hover:bg-[#e8ebf0]'
          } ${isOpen ? 'bg-white border-indigo-600 ring-2 ring-indigo-500/20 shadow-sm' : ''}`}
      >
        <span className={value ? 'text-gray-900 font-medium' : 'text-gray-500'}>
          {value || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180 text-gray-800' : ''
            }`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-30 w-full bg-white border border-gray-200/90 rounded-xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.14)] p-1.5 max-h-56 overflow-auto transition-all animate-in fade-in zoom-in-95 duration-150 ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
            }`}
        >
          {options.map((option) => {
            const isOther = option === 'Other';
            const isSelected = value === option;

            return (
              <React.Fragment key={option}>
                {isOther && <div className="my-1 border-t border-gray-100" />}
                <button
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm transition-colors ${isSelected
                    ? 'bg-indigo-50/90 text-indigo-900 font-semibold'
                    : isOther
                      ? 'text-indigo-600 hover:bg-[#eef0f4] font-medium'
                      : 'text-gray-800 hover:bg-[#eef0f4] font-medium'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    {isOther && <PlusCircle size={14} className="text-indigo-500" />}
                    <span>{option}</span>
                  </div>
                  {isSelected && <Check size={16} className="text-indigo-600 stroke-[2.5]" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

const VeterinarianInfoForm = ({ onNext, onBack, initialData }) => {
  // Phone state
  const [countryCode, setCountryCode] = useState('+91');
  const [localPhone, setLocalPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // License file state preserved for future logic if needed
  const [licenseFile] = useState(initialData?.licenseFile || null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || { role: 'veterinarian' },
  });

  // Register custom select fields with react-hook-form validation
  useEffect(() => {
    register('professionalCategory', { required: 'Category is required' });
    register('primaryPractice', { required: 'Primary practice is required' });
    register('jobRole', { required: 'Job Role is required' });
  }, [register]);

  const selectedCategory = watch('professionalCategory');
  const selectedPractice = watch('primaryPractice');
  const selectedJobRole = watch('jobRole');

  const [clinicSearchTerm, setClinicSearchTerm] = useState(initialData?.clinicName || '');
  const [clinicSuggestions, setClinicSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const [isSuggestingClinic, setIsSuggestingClinic] = useState(false);
  const clinicRef = useRef(null);

  // Pre-fill phone from initialData
  useEffect(() => {
    if (initialData?.phone) {
      const matched = COUNTRY_CODES.find((c) => initialData.phone.startsWith(c.code));
      if (matched) {
        setCountryCode(matched.code);
        setLocalPhone(initialData.phone.slice(matched.code.length));
      } else {
        setLocalPhone(initialData.phone.replace(/^\+/, ''));
      }
    }
  }, [initialData?.phone]);

  useEffect(() => {
    if (clinicSearchTerm.trim().length < 2) {
      setClinicSuggestions([]);
      setShowNoResults(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await axiosInstance.get(`/vet-clinics/search?q=${encodeURIComponent(clinicSearchTerm.trim())}`);
        const results = response?.data?.data || [];
        setClinicSuggestions(results);
        setShowNoResults(results.length === 0);
      } catch (error) {
        console.error('Error fetching clinics:', error);
        setClinicSuggestions([]);
        setShowNoResults(true);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [clinicSearchTerm]);

  // Click outside listener for clinic dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clinicRef.current && !clinicRef.current.contains(event.target)) {
        setClinicSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clinicRef]);

  // Called when user picks a result from the dropdown
  const handleSelectClinic = (clinic) => {
    const label = clinic.name;
    setClinicSearchTerm(label);
    setValue('clinicName', label, { shouldValidate: true });
    setClinicSuggestions([]);
    setShowNoResults(false);
  };

  const handleClinicChange = (e) => {
    const val = typeof e === 'string' ? e : (e?.target?.value ?? '');
    setClinicSearchTerm(val);
    setValue('clinicName', val, { shouldValidate: true });
    setShowNoResults(false);
  };

  const handleEnterManualMode = () => {
    setManualMode(true);
    setClinicSuggestions([]);
    setShowNoResults(false);
  };

  const handleCancelManual = () => {
    setManualMode(false);
    setManualName('');
    setManualCity('');
    setManualState('');
  };

  const handleManualSubmit = async () => {
    if (!manualName.trim()) return;
    setIsSuggestingClinic(true);
    try {
      await axiosInstance.post('/vet-clinics/suggest', {
        name: manualName.trim(),
        city: manualCity.trim() || undefined,
        state: manualState.trim() || undefined,
      });
      // Use the manually entered name as the clinic value
      const displayName = manualName.trim();
      setClinicSearchTerm(displayName);
      setValue('clinicName', displayName, { shouldValidate: true });
      setManualMode(false);
      setManualName('');
      setManualCity('');
      setManualState('');
    } catch (err) {
      console.error('Error submitting clinic suggestion:', err);
      // Still set the value so the user can continue registration
      const displayName = manualName.trim();
      setClinicSearchTerm(displayName);
      setValue('clinicName', displayName, { shouldValidate: true });
      setManualMode(false);
    } finally {
      setIsSuggestingClinic(false);
    }
  };

  const validatePhone = () => {
    if (!localPhone) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (!/^[0-9]{10}$/.test(localPhone)) {
      setPhoneError('Enter a valid 10-digit phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const onSubmit = (data) => {
    if (!validatePhone()) return;

    // Handle 'Other' custom entries
    if (data.professionalCategory === 'Other') {
      data.professionalCategory = data.customProfessionalCategory || 'Other';
    }
    if (data.primaryPractice === 'Other') {
      data.primaryPractice = data.customPrimaryPractice || 'Other';
    }
    if (data.jobRole === 'Other') {
      data.jobRole = data.customJobRole || 'Other';
    }

    data.specializations = [];
    data.clinicName = data.clinicName || clinicSearchTerm;
    data.phone = `${countryCode}${localPhone}`;
    data.licenseFile = licenseFile || null;
    onNext(data);
  };

  return (
    <div className="animate-fade-in">
      {onBack && (
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      )}

      <h2 className="text-2xl font-bold text-gray-900 tracking-tight mt-2">Your Information</h2>
      <p className="text-gray-600 mb-6 text-sm font-medium">Tell us a bit about yourself</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('firstName', { 
                required: 'First name is required',
                validate: {
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed'
                }
              })}
              placeholder="John"
              className="w-full px-3.5 py-2.5 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('lastName', { 
                required: 'Last name is required',
                validate: {
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed'
                }
              })}
              placeholder="Doe"
              className="w-full px-3.5 py-2.5 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              },
              validate: {
                noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in email'
              }
            })}
            placeholder="john.doe@email.com"
            className="w-full px-3.5 py-2.5 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        {/* Phone with Country Code */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div
            className={`flex rounded-xl overflow-hidden border bg-[#eef0f4] focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-600 focus-within:bg-white transition-all ${phoneError ? 'border-red-500' : 'border-gray-300/80'
              }`}
          >
            <div className="relative flex-shrink-0">
              <select
                id="countryCode"
                value={countryCode}
                onChange={(e) => setCountryCode(e?.target?.value ?? e ?? '+91')}
                className="h-full pl-3.5 pr-7 py-2.5 bg-transparent border-r border-gray-300/80 text-sm font-semibold text-gray-800 focus:outline-none appearance-none cursor-pointer"
                style={{ minWidth: '94px' }}
              >
                {COUNTRY_CODES.map((c, i) => (
                  <option key={`${c.code}-${i}`} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <ChevronDown size={12} className="text-gray-600" />
              </div>
            </div>

            <input
              id="localPhone"
              type="tel"
              value={localPhone}
              onChange={(e) => {
                const raw = e?.target?.value ?? e ?? '';
                let val = String(raw).replace(/\D/g, '');
                if (val.startsWith('0')) {
                  val = val.replace(/^0+/, '');
                }
                if (val.length > 10) {
                  val = val.substring(0, 10);
                }
                setLocalPhone(val);
                if (phoneError) setPhoneError('');
              }}
              placeholder="9999999999"
              maxLength={11}
              className="flex-1 px-3.5 py-2.5 bg-transparent text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none min-w-0"
            />
          </div>
          {phoneError && <p className="mt-1 text-xs text-red-500">{phoneError}</p>}
        </div>

        {/* Professional Experience Section */}
        <div className="pt-2">
          <h3 className="text-md font-bold text-gray-900 border-t border-gray-200 pt-3 mb-3">
            Professional Experience
          </h3>
        </div>

        {/* Professional Category */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Professional Category <span className="text-red-500">*</span>
          </label>
          {selectedCategory === 'Other' ? (
            <div className="relative">
              <input
                {...register('customProfessionalCategory', { required: 'Please specify your category' })}
                placeholder="Specify your category"
                className="w-full px-3.5 py-2.5 pr-10 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setValue('professionalCategory', '', { shouldValidate: true });
                  setValue('customProfessionalCategory', '');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition-colors p-1"
                title="Reset to dropdown"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <CustomSelect
              options={PROFESSIONAL_CATEGORIES}
              value={selectedCategory}
              onChange={(val) => setValue('professionalCategory', val, { shouldValidate: true })}
              placeholder="Select your category"
              error={errors.professionalCategory}
            />
          )}
          {errors.professionalCategory && <p className="mt-1 text-xs text-red-500">{errors.professionalCategory.message}</p>}
          {errors.customProfessionalCategory && (
            <p className="mt-1 text-xs text-red-500">{errors.customProfessionalCategory.message}</p>
          )}
        </div>

        {/* Grid: Years of Experience & Primary Practice */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Years of Experience <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              placeholder="5"
              {...register('yearsOfExperience', { required: 'Required', valueAsNumber: true })}
              className="w-full px-3.5 py-2.5 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
            />
            {errors.yearsOfExperience && <p className="mt-1 text-xs text-red-500">{errors.yearsOfExperience.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Primary Practice <span className="text-red-500">*</span>
            </label>
            {selectedPractice === 'Other' ? (
              <div className="relative">
                <input
                  {...register('customPrimaryPractice', { required: 'Please specify practice' })}
                  placeholder="Specify practice"
                  className="w-full px-3.5 py-2.5 pr-10 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setValue('primaryPractice', '', { shouldValidate: true });
                    setValue('customPrimaryPractice', '');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition-colors p-1"
                  title="Reset to dropdown"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <CustomSelect
                options={PRIMARY_PRACTICES}
                value={selectedPractice}
                onChange={(val) => setValue('primaryPractice', val, { shouldValidate: true })}
                placeholder="Select primary practice"
                error={errors.primaryPractice}
              />
            )}
            {errors.primaryPractice && <p className="mt-1 text-xs text-red-500">{errors.primaryPractice.message}</p>}
            {errors.customPrimaryPractice && (
              <p className="mt-1 text-xs text-red-500">{errors.customPrimaryPractice.message}</p>
            )}
          </div>
        </div>

        {/* Associated Clinic / Hospital */}
        <div className="relative" ref={clinicRef}>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Associated Clinic/Hospital <span className="text-red-500">*</span>
          </label>

          {!manualMode ? (
            <>
              <div className="relative">
                <input
                  {...register('clinicName', { required: 'Clinic name is required' })}
                  value={clinicSearchTerm}
                  onChange={handleClinicChange}
                  placeholder="Type to search your clinic or hospital"
                  autoComplete="off"
                  className="w-full px-3.5 py-2.5 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all pr-9"
                />
                {isSearching ? (
                  <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-600 animate-spin" />
                ) : (
                  <MapPin className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
                )}
              </div>
              {errors.clinicName && <p className="mt-1 text-xs text-red-500">{errors.clinicName.message}</p>}

              {/* Results dropdown */}
              {(clinicSuggestions.length > 0 || showNoResults) && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                  {clinicSuggestions.length > 0 && (
                    <ul className="max-h-48 overflow-auto">
                      {clinicSuggestions.map((clinic) => (
                        <li
                          key={clinic.id}
                          onClick={() => handleSelectClinic(clinic)}
                          className="px-3.5 py-2.5 hover:bg-[#eef0f4] cursor-pointer text-sm border-b border-gray-100 last:border-0 transition-colors"
                        >
                          <p className="font-semibold text-gray-900 truncate text-[13px]">{clinic.name}</p>
                          {(clinic.address || clinic.city) && (
                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                              {[clinic.address, clinic.city, clinic.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Can't find clinic footer — always visible when dropdown is open */}
                  <button
                    type="button"
                    onClick={handleEnterManualMode}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-[12px] text-indigo-600 font-semibold hover:bg-indigo-50 transition-colors border-t border-gray-100"
                  >
                    <PlusCircle size={13} />
                    Can't find your clinic? Add it manually
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Manual add inline form */
            <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-3.5 space-y-2.5">
              <p className="text-xs font-semibold text-indigo-700 mb-1">Add your clinic manually</p>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Clinic / Hospital Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="e.g. Apollo Veterinary Clinic"
                  autoFocus
                  className="w-full px-3 py-2 bg-white border border-gray-300/80 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">City <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={manualCity}
                    onChange={(e) => setManualCity(e.target.value)}
                    placeholder="Mumbai"
                    className="w-full px-3 py-2 bg-white border border-gray-300/80 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">State <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={manualState}
                    onChange={(e) => setManualState(e.target.value)}
                    placeholder="Maharashtra"
                    className="w-full px-3 py-2 bg-white border border-gray-300/80 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleManualSubmit}
                  disabled={!manualName.trim() || isSuggestingClinic}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSuggestingClinic ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  Save &amp; Use This Clinic
                </button>
                <button
                  type="button"
                  onClick={handleCancelManual}
                  className="py-2 px-3 text-xs text-gray-500 hover:text-gray-800 font-medium transition-colors rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Job Role */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Job Role <span className="text-red-500">*</span>
          </label>
          {selectedJobRole === 'Other' ? (
            <div className="relative">
              <input
                {...register('customJobRole', { required: 'Please specify your job role' })}
                placeholder="Specify your job role"
                className="w-full px-3.5 py-2.5 pr-10 bg-[#eef0f4] border border-gray-300/80 rounded-xl text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setValue('jobRole', '', { shouldValidate: true });
                  setValue('customJobRole', '');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition-colors p-1"
                title="Reset to dropdown"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <CustomSelect
              options={JOB_ROLES}
              value={selectedJobRole}
              onChange={(val) => setValue('jobRole', val, { shouldValidate: true })}
              placeholder="Select your role"
              error={errors.jobRole}
              dropUp={true}
            />
          )}
          {errors.jobRole && <p className="mt-1 text-xs text-red-500">{errors.jobRole.message}</p>}
          {errors.customJobRole && <p className="mt-1 text-xs text-red-500">{errors.customJobRole.message}</p>}
        </div>

        <button
          type="submit"
          className="mt-6 w-full flex items-center justify-center py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-sm"
        >
          Continue
        </button>
      </form>
    </div>
  );
};

export default VeterinarianInfoForm;
